class ArticleTOC {
  constructor() {
    this.tocContainer = document.querySelector('.article-toc');
    this.tocInline = document.querySelector('.article-toc__inline');
    this.tocSticky = document.querySelector('.article-toc__sticky');
    this.tocPanel = document.querySelector('.article-toc__panel');
    this.tocToggle = document.querySelector('.article-toc__toggle');
    this.progressBar = document.querySelector('.article-toc__progress-bar');
    this.progressText = document.querySelector('.article-toc__progress-text');
    this.backToTopBtn = document.querySelector('.article-toc__back-to-top');
    this.articleContent = document.querySelector('.article-template__content');
    this.headings = [];
    this.isOpen = false;
    this.inlineTOCOffset = 0;
    this.showInline = this.tocContainer?.dataset.showInline !== 'false';
    this.showSlideout = this.tocContainer?.dataset.showSlideout !== 'false';
    
    if (!this.tocContainer) {
      console.warn('ArticleTOC: TOC container not found');
      return;
    }
    
    if (!this.articleContent) {
      console.warn('ArticleTOC: Article content not found');
      return;
    }
    
    this.init();
  }
  
  init() {
    this.addHeadingIds();
    this.buildTOC();
    this.setupScrollListener();
    this.setupClickHandlers();
    this.setupProgressTracking();
    
    // Calculate inline TOC offset for sticky behavior
    if (this.tocInline && this.showInline) {
      this.inlineTOCOffset = this.tocInline.offsetTop + this.tocInline.offsetHeight;
    } else if (!this.showInline && this.showSlideout) {
      // Slideout-only mode: show sticky immediately
      this.inlineTOCOffset = 0;
    }
  }
  
  addHeadingIds() {
    const headings = this.articleContent.querySelectorAll('h2, h3');
    headings.forEach((heading, index) => {
      if (!heading.id) {
        const text = heading.textContent.trim();
        const id = this.slugify(text) || `heading-${index}`;
        heading.id = id;
      }
    });
  }
  
  slugify(text) {
    return text
      .toLowerCase()
      .replace(/[^\w\s-]/g, '')
      .replace(/\s+/g, '-')
      .replace(/-+/g, '-')
      .trim();
  }
  
  buildTOC() {
    // Check if H3 headings should be included
    const includeH3 = this.tocContainer?.dataset.includeH3 !== 'false';
    const minHeadings = parseInt(this.tocContainer?.dataset.minHeadings || '3', 10);
    
    // Get headings based on settings
    const selector = includeH3 ? 'h2, h3' : 'h2';
    const headings = this.articleContent.querySelectorAll(selector);
    
    console.log('ArticleTOC: Found', headings.length, 'headings with selector', selector);
    
    if (headings.length < minHeadings) {
      console.log('ArticleTOC: Not enough headings (', headings.length, '<', minHeadings, '), hiding TOC');
      if (this.tocContainer) this.tocContainer.style.display = 'none';
      return;
    }
    
    this.headings = Array.from(headings);
    const tocList = this.createTOCList(this.headings);
    
    if (this.tocInline) {
      const inlineList = this.tocInline.querySelector('ul');
      if (inlineList) {
        inlineList.innerHTML = tocList;
      }
    }
    
    if (this.tocPanel) {
      const panelList = this.tocPanel.querySelector('ul');
      if (panelList) {
        panelList.innerHTML = tocList;
      }
    }
    
    this.setupTOCLinks();
  }
  
  createTOCList(headings) {
    let html = '';
    
    headings.forEach((heading) => {
      const id = heading.id;
      const text = heading.textContent.trim();
      const level = heading.tagName.toLowerCase();
      
      if (level === 'h2') {
        html += `<li><a href="#${id}" data-heading-id="${id}" class="article-toc__h2">${text}</a></li>`;
      } else if (level === 'h3') {
        html += `<li><a href="#${id}" data-heading-id="${id}" class="article-toc__h3">${text}</a></li>`;
      }
    });
    
    return html;
  }
  
  setupTOCLinks() {
    const links = this.tocContainer.querySelectorAll('a[data-heading-id]');
    links.forEach((link) => {
      link.addEventListener('click', (e) => {
        e.preventDefault();
        const targetId = link.getAttribute('data-heading-id');
        const targetElement = document.getElementById(targetId);
        
        if (targetElement) {
          const offset = 100; // Offset for fixed header
          const elementPosition = targetElement.getBoundingClientRect().top;
          const offsetPosition = elementPosition + window.pageYOffset - offset;
          
          window.scrollTo({
            top: offsetPosition,
            behavior: 'smooth'
          });
          
          // Close sticky panel on mobile after clicking
          if (window.innerWidth < 990 && this.isOpen) {
            this.toggleStickyPanel();
          }
          
          // Update active state after scroll
          setTimeout(() => this.updateActiveHeading(), 500);
        }
      });
    });
  }
  
  setupScrollListener() {
    let ticking = false;
    
    window.addEventListener('scroll', () => {
      if (!ticking) {
        window.requestAnimationFrame(() => {
          this.handleScroll();
          ticking = false;
        });
        ticking = true;
      }
    });
    
    // Initial check
    this.handleScroll();
  }
  
  handleScroll() {
    const scrollY = window.scrollY || window.pageYOffset;
    
    // Show/hide sticky TOC based on scroll position
    if (this.tocSticky) {
      if (!this.showInline && this.showSlideout) {
        // Slideout-only mode: always visible once page has any scroll or immediately
        this.tocSticky.classList.add('is-visible');
        this.tocSticky.removeAttribute('aria-hidden');
      } else if (this.inlineTOCOffset > 0) {
        if (scrollY > this.inlineTOCOffset) {
          this.tocSticky.classList.add('is-visible');
          if (!this.isOpen) {
            this.tocSticky.removeAttribute('aria-hidden');
          }
        } else {
          this.tocSticky.classList.remove('is-visible');
          this.tocSticky.setAttribute('aria-hidden', 'true');
        }
      }
    }
    
    // Show/hide back to top button
    if (this.backToTopBtn) {
      if (scrollY > 300) {
        this.backToTopBtn.classList.add('is-visible');
      } else {
        this.backToTopBtn.classList.remove('is-visible');
      }
    }
    
    this.updateActiveHeading();
  }
  
  updateActiveHeading() {
    const scrollY = window.scrollY || window.pageYOffset;
    const offset = 150;
    
    let activeHeading = null;
    
    // Find the heading that's currently in view
    for (let i = this.headings.length - 1; i >= 0; i--) {
      const heading = this.headings[i];
      const rect = heading.getBoundingClientRect();
      const headingTop = rect.top + scrollY;
      
      if (headingTop <= scrollY + offset) {
        activeHeading = heading.id;
        break;
      }
    }
    
    // Update active state
    const links = this.tocContainer.querySelectorAll('a[data-heading-id]');
    links.forEach((link) => {
      if (link.getAttribute('data-heading-id') === activeHeading) {
        link.classList.add('is-active');
      } else {
        link.classList.remove('is-active');
      }
    });
  }
  
  setupClickHandlers() {
    // Toggle sticky panel
    if (this.tocToggle) {
      this.tocToggle.addEventListener('click', (e) => {
        e.preventDefault();
        e.stopPropagation();
        this.toggleStickyPanel();
      });
    }
    
    // Close button
    const closeBtn = this.tocContainer?.querySelector('.article-toc__close');
    if (closeBtn) {
      closeBtn.addEventListener('click', (e) => {
        e.preventDefault();
        e.stopPropagation();
        if (this.isOpen) {
          this.toggleStickyPanel();
        }
      });
    }
    
    // Close panel when clicking outside
    document.addEventListener('click', (e) => {
      if (this.isOpen && this.tocPanel && !this.tocPanel.contains(e.target) && !this.tocToggle.contains(e.target)) {
        this.toggleStickyPanel();
      }
    });
    
    // Back to top
    if (this.backToTopBtn) {
      this.backToTopBtn.addEventListener('click', (e) => {
        e.preventDefault();
        window.scrollTo({
          top: 0,
          behavior: 'smooth'
        });
      });
    }
  }
  
  toggleStickyPanel() {
    if (!this.tocSticky || !this.tocPanel) return;
    
    this.isOpen = !this.isOpen;
    
    if (this.isOpen) {
      this.tocSticky.classList.add('is-open');
      this.tocPanel.setAttribute('aria-hidden', 'false');
      if (this.tocToggle) {
        this.tocToggle.setAttribute('aria-expanded', 'true');
      }
      // Remove aria-hidden from sticky container when panel is open
      this.tocSticky.removeAttribute('aria-hidden');
    } else {
      this.tocSticky.classList.remove('is-open');
      this.tocPanel.setAttribute('aria-hidden', 'true');
      if (this.tocToggle) {
        this.tocToggle.setAttribute('aria-expanded', 'false');
      }
      // Only hide sticky container from screen readers when panel is closed and not visible
      if (!this.tocSticky.classList.contains('is-visible')) {
        this.tocSticky.setAttribute('aria-hidden', 'true');
      }
    }
  }
  
  setupProgressTracking() {
    if (!this.articleContent) return;
    
    const progressBars = document.querySelectorAll('.article-toc__progress-bar');
    if (progressBars.length === 0) return;
    
    const updateProgress = () => {
      const articleTop = this.articleContent.offsetTop;
      const articleHeight = this.articleContent.offsetHeight;
      const windowHeight = window.innerHeight;
      const scrollY = window.scrollY || window.pageYOffset;
      
      const scrollableHeight = articleHeight + articleTop - windowHeight;
      const scrolled = Math.max(0, scrollY - articleTop);
      const progress = Math.min(100, Math.max(0, (scrolled / scrollableHeight) * 100));
      
      // Update all progress bars (toggle button and panel)
      progressBars.forEach((bar) => {
        bar.style.width = `${progress}%`;
      });
      
      if (this.progressText) {
        this.progressText.textContent = `${Math.round(progress)}%`;
      }
    };
    
    window.addEventListener('scroll', () => {
      window.requestAnimationFrame(updateProgress);
    });
    
    // Initial update
    updateProgress();
  }
}

// Initialize when DOM is ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => {
    new ArticleTOC();
  });
} else {
  new ArticleTOC();
}

